import React, { useState, useEffect, useRef } from 'react';
import { searchUsers, createProject } from '../../../utils/api';
import Modal from '../../common/Modal';
import './NewProjectModal.scss';

const NewProjectModal = ({ isOpen, onClose, categories, onProjectCreated }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: '',
        assignees: []
    });
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const userSearchRef = useRef(null);
    const dropdownRef = useRef(null);

    // Debounce user search
    useEffect(() => {
        if (!userSearchQuery.trim()) {
            setSearchResults([]);
            setShowUserDropdown(false);
            return;
        }

        const debounceTimer = setTimeout(async () => {
            setIsSearching(true);
            try {
                const response = await searchUsers({ search: userSearchQuery, page: 1, size: 10 });
                if (response && response.data && response.data.users) {
                    const users = response.data.users.users || [];
                    // Filter out already selected users
                    const filteredUsers = users.filter(user => 
                        !formData.assignees.some(assignee => 
                            (assignee.ID || assignee.id) === (user.ID || user.id)
                        )
                    );
                    setSearchResults(filteredUsers);
                    setShowUserDropdown(true);
                } else {
                    setSearchResults([]);
                    setShowUserDropdown(false);
                }
            } catch (err) {
                console.error('Error searching users:', err);
                setSearchResults([]);
                setShowUserDropdown(false);
            } finally {
                setIsSearching(false);
            }
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [userSearchQuery, formData.assignees]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current && 
                !dropdownRef.current.contains(event.target) &&
                userSearchRef.current &&
                !userSearchRef.current.contains(event.target)
            ) {
                setShowUserDropdown(false);
            }
        };

        if (showUserDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showUserDropdown]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError(null);
    };

    const handleAddUser = (user) => {
        if (!formData.assignees.some(assignee => 
            (assignee.ID || assignee.id) === (user.ID || user.id)
        )) {
            setFormData(prev => ({
                ...prev,
                assignees: [...prev.assignees, user]
            }));
        }
        setUserSearchQuery('');
        setShowUserDropdown(false);
    };

    const handleRemoveUser = (userId) => {
        setFormData(prev => ({
            ...prev,
            assignees: prev.assignees.filter(assignee => 
                (assignee.ID || assignee.id) !== userId
            )
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!formData.title.trim()) {
            setError('Project name is required');
            return;
        }

        setIsSubmitting(true);

        try {
            const projectData = {
                title: formData.title.trim(),
                description: formData.description.trim() || undefined,
                category: formData.category || undefined,
                assignees: formData.assignees.map(user => user.ID || user.id)
            };

            const response = await createProject(projectData);

            if (response && response.data) {
                // Reset form
                setFormData({
                    title: '',
                    description: '',
                    category: '',
                    assignees: []
                });
                setUserSearchQuery('');
                setError(null);
                
                // Notify parent component
                if (onProjectCreated) {
                    onProjectCreated(response.data);
                }
                
                // Close modal
                onClose();
            } else {
                setError('Failed to create project. Please try again.');
            }
        } catch (err) {
            console.error('Error creating project:', err);
            setError(err.message || 'Failed to create project. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setFormData({
                title: '',
                description: '',
                category: '',
                assignees: []
            });
            setUserSearchQuery('');
            setError(null);
            setShowUserDropdown(false);
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Create New Project"
            size="medium"
        >
            <form onSubmit={handleSubmit} className="octopus-new-project-form">
                {error && (
                    <div className="octopus-form-error">
                        {error}
                    </div>
                )}

                <div className="octopus-form-group">
                    <label htmlFor="project-title">Name of the project *</label>
                    <input
                        type="text"
                        id="project-title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="Name of the project"
                        required
                        disabled={isSubmitting}
                    />
                </div>

                <div className="octopus-form-group">
                    <label htmlFor="project-category">Project Category</label>
                    <select
                        id="project-category"
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                    >
                        <option value="">- Project Category -</option>
                        {categories.map((category) => (
                            <option 
                                key={category.id || category.category_id} 
                                value={category.id || category.category_id}
                            >
                                {category.name || category.title}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="octopus-form-group">
                    <label htmlFor="project-description">Some details about the project (optional)</label>
                    <textarea
                        id="project-description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Some details about the project (optional)"
                        rows="4"
                        disabled={isSubmitting}
                    />
                </div>

                <div className="octopus-form-group">
                    <label htmlFor="project-users">Search users...</label>
                    <div className="octopus-user-search-wrapper" ref={userSearchRef}>
                        <input
                            type="text"
                            id="project-users"
                            value={userSearchQuery}
                            onChange={(e) => setUserSearchQuery(e.target.value)}
                            onFocus={() => {
                                if (searchResults.length > 0) {
                                    setShowUserDropdown(true);
                                }
                            }}
                            placeholder="Search users..."
                            disabled={isSubmitting}
                        />
                        {showUserDropdown && (
                            <div className="octopus-user-search-dropdown" ref={dropdownRef}>
                                {isSearching ? (
                                    <div className="octopus-search-loading">
                                        <i className="fa-solid fa-spinner fa-spin"></i> Searching...
                                    </div>
                                ) : searchResults.length > 0 ? (
                                    searchResults.map((user) => (
                                        <div
                                            key={user.ID || user.id}
                                            className="octopus-user-search-item"
                                            onClick={() => handleAddUser(user)}
                                        >
                                            <div className="octopus-user-avatar">
                                                {user.image ? (
                                                    <img src={user.image} alt={user.display_name || user.name} />
                                                ) : (
                                                    <div className="octopus-user-avatar-placeholder">
                                                        {(user.display_name || user.name || 'U').charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="octopus-user-info">
                                                <div className="octopus-user-name">
                                                    {user.display_name || user.name || 'User'}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="octopus-search-empty">
                                        No users found
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {formData.assignees.length > 0 && (
                        <div className="octopus-selected-users">
                            {formData.assignees.map((user) => (
                                <div key={user.ID || user.id} className="octopus-selected-user-tag">
                                    <div className="octopus-user-avatar-small">
                                        {user.image ? (
                                            <img src={user.image} alt={user.display_name || user.name} />
                                        ) : (
                                            <div className="octopus-user-avatar-placeholder">
                                                {(user.display_name || user.name || 'U').charAt(0).toUpperCase()}
                                            </div>
                                        )}
                                    </div>
                                    <span>{user.display_name || user.name || 'User'}</span>
                                    <button
                                        type="button"
                                        className="octopus-remove-user"
                                        onClick={() => handleRemoveUser(user.ID || user.id)}
                                        disabled={isSubmitting}
                                    >
                                        <i className="fa-solid fa-times"></i>
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="octopus-form-actions">
                    <button
                        type="button"
                        className="octopus-btn octopus-btn-secondary"
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="octopus-btn octopus-btn-primary"
                        disabled={isSubmitting || !formData.title.trim()}
                    >
                        {isSubmitting ? (
                            <>
                                <i className="fa-solid fa-spinner fa-spin"></i> Creating...
                            </>
                        ) : (
                            'Create Project'
                        )}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default NewProjectModal;

